import { useState, useEffect, useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
  addMonths,
  parseISO,
} from 'date-fns';
import {
  Plus,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Calendar,
  DollarSign,
  TrendingDown,
  Tag,
  Receipt,
  Search,
  X,
  AlertCircle,
  IndianRupee,
  ArrowUpDown,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Input,
  Select,
  Modal,
  LoadingSpinner,
} from '../components/ui';
import {
  getExpenses,
  addExpense,
  updateExpense,
  deleteExpense,
  getSettings,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { Expense, ExpenseFormData, Settings } from '../types';

const EXPENSE_CATEGORIES = [
  'Groceries',
  'Dining Out',
  'Transportation',
  'Utilities',
  'Entertainment',
  'Healthcare',
  'Shopping',
  'Education',
  'Travel',
  'Rent',
  'Insurance',
  'Personal Care',
  'Gifts',
  'Other',
];

const categoryIcons: Record<string, string> = {
  Groceries: '🛒',
  'Dining Out': '🍔',
  Transportation: '🚗',
  Utilities: '💡',
  Entertainment: '🎬',
  Healthcare: '🏥',
  Shopping: '🛍️',
  Education: '📚',
  Travel: '✈️',
  Rent: '🏠',
  Insurance: '🛡️',
  'Personal Care': '💅',
  Gifts: '🎁',
  Other: '📦',
};

const categoryBarColors: string[] = [
  'bg-blue-500',
  'bg-purple-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-lime-500',
  'bg-fuchsia-500',
  'bg-sky-500',
  'bg-red-500',
];

const currencySymbols: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  AED: 'د.إ',
};

type SortField = 'date' | 'amount' | 'category';
type SortDir = 'asc' | 'desc';

const getInitialFormData = (): ExpenseFormData => ({
  description: '',
  amount: 0,
  currency: 'INR',
  category: '',
  expenseDate: format(new Date(), 'yyyy-MM-dd'),
});

export default function ExpenseTracker() {
  const { user } = useAuth();
  const isFamilyMember = Boolean(user?.isFamilyMember);

  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});

  // Date navigation
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [formData, setFormData] = useState<ExpenseFormData>(getInitialFormData());
  const [saving, setSaving] = useState(false);

  // Search + Sort
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    fetchSettings();
    fetchExchangeRates();
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [currentMonth, useCustomRange, customStart, customEnd]);

  const fetchSettings = async () => {
    try {
      const res = await getSettings();
      if (res.success) {
        setSettings(res.settings);
      }
    } catch {}
  };

  // Fetch exchange rates with INR as the pivot (matches Settings page pattern)
  const fetchExchangeRates = async () => {
    const codes = Object.keys(currencySymbols).filter((c) => c !== 'INR');
    const results = await Promise.all(
      codes.map(async (code) => {
        try {
          const res = await fetch(`https://api.frankfurter.app/latest?from=${code}&to=INR`);
          if (res.ok) {
            const data = (await res.json()) as { rates?: Record<string, number> };
            const rate = data?.rates?.INR;
            if (typeof rate === 'number') return { code, rate };
          }
        } catch {}
        try {
          const res = await fetch(`https://open.er-api.com/v6/latest/${code}`);
          if (res.ok) {
            const data = (await res.json()) as { rates?: Record<string, number> };
            const rate = data?.rates?.INR;
            if (typeof rate === 'number') return { code, rate };
          }
        } catch {}
        return null;
      })
    );
    // rates map: { USD: 84.5, EUR: 92.1, ... } meaning 1 USD = 84.5 INR
    const rateMap: Record<string, number> = { INR: 1 };
    for (const r of results) {
      if (r) rateMap[r.code] = r.rate;
    }
    setExchangeRates(rateMap);
  };

  /**
   * Convert an amount from `fromCurrency` to `toCurrency` using INR as pivot.
   * rates map stores "1 X = ? INR".
   * To convert X→Y: amount * (rateX / rateY)
   */
  const convertAmount = (amount: number, fromCurrency: string, toCurrency: string): number => {
    if (fromCurrency === toCurrency) return amount;
    const fromRate = exchangeRates[fromCurrency];
    const toRate = exchangeRates[toCurrency];
    if (!fromRate || !toRate) return amount; // rates not loaded yet, show as-is
    return Math.round((amount * (fromRate / toRate)) * 100) / 100;
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      setError(null);

      let startDate: string;
      let endDate: string;

      if (useCustomRange && customStart && customEnd) {
        startDate = customStart;
        endDate = customEnd;
      } else {
        startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      }

      const res = await getExpenses(startDate, endDate);
      if (res.success) {
        setExpenses(res.expenses);
      } else {
        setError(res.error || 'Failed to fetch expenses');
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const displayCurrency = settings?.currency || 'INR';
  const sym = currencySymbols[displayCurrency] || displayCurrency;

  const categoryOptions = useMemo(() => {
    const cats = settings?.categories?.length ? settings.categories : EXPENSE_CATEGORIES;
    return cats.map((c) => ({ value: c, label: c }));
  }, [settings]);

  // Convert all expense amounts to the display currency
  const convertedExpenses = useMemo(() => {
    return expenses.map((e) => ({
      ...e,
      displayAmount: convertAmount(e.amount, e.currency, displayCurrency),
    }));
  }, [expenses, exchangeRates, displayCurrency]);

  // Recompute total / byCategory using converted amounts
  const total = useMemo(() => {
    return Math.round(convertedExpenses.reduce((sum, e) => sum + e.displayAmount, 0) * 100) / 100;
  }, [convertedExpenses]);

  const byCategory = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of convertedExpenses) {
      const cat = e.category || 'Uncategorized';
      map[cat] = (map[cat] || 0) + e.displayAmount;
    }
    return map;
  }, [convertedExpenses]);

  // Filtered + sorted expenses
  const displayExpenses = useMemo(() => {
    let filtered = convertedExpenses;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.description.toLowerCase().includes(q) ||
          e.category.toLowerCase().includes(q) ||
          e.ownerName.toLowerCase().includes(q)
      );
    }
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'date') cmp = a.expenseDate.localeCompare(b.expenseDate);
      else if (sortField === 'amount') cmp = a.displayAmount - b.displayAmount;
      else cmp = a.category.localeCompare(b.category);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [convertedExpenses, searchQuery, sortField, sortDir]);

  // Category breakdown sorted by amount
  const sortedCategories = useMemo(() => {
    return Object.entries(byCategory)
      .sort(([, a], [, b]) => b - a)
      .map(([cat, amount], idx) => ({
        category: cat,
        amount,
        percentage: total > 0 ? (amount / total) * 100 : 0,
        color: categoryBarColors[idx % categoryBarColors.length],
        icon: categoryIcons[cat] || '📦',
      }));
  }, [byCategory, total]);

  const handleAdd = async () => {
    if (!formData.description.trim() || formData.amount <= 0) return;
    try {
      setSaving(true);
      const res = await addExpense({ ...formData, currency: displayCurrency });
      if (res.success && res.expense) {
        setExpenses((prev) => [res.expense!, ...prev]);
        await fetchExpenses();
        setShowAddModal(false);
        setFormData(getInitialFormData());
      } else {
        setError(res.error || 'Failed to add expense');
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedExpense || !formData.description.trim() || formData.amount <= 0) return;
    try {
      setSaving(true);
      const res = await updateExpense(selectedExpense.id, { ...formData, currency: displayCurrency });
      if (res.success && res.expense) {
        await fetchExpenses();
        setShowEditModal(false);
        setSelectedExpense(null);
        setFormData(getInitialFormData());
      } else {
        setError(res.error || 'Failed to update expense');
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedExpense) return;
    try {
      setSaving(true);
      const res = await deleteExpense(selectedExpense.id);
      if (res.success) {
        await fetchExpenses();
        setShowDeleteModal(false);
        setSelectedExpense(null);
      } else {
        setError(res.error || 'Failed to delete expense');
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (expense: Expense) => {
    setSelectedExpense(expense);
    setFormData({
      description: expense.description,
      amount: expense.amount,
      currency: expense.currency,
      category: expense.category,
      expenseDate: expense.expenseDate,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (expense: Expense) => {
    setSelectedExpense(expense);
    setShowDeleteModal(true);
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const prevMonth = () => setCurrentMonth((m) => subMonths(m, 1));
  const nextMonth = () => setCurrentMonth((m) => addMonths(m, 1));

  const formatAmount = (amount: number) =>
    `${sym}${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Expense Form (shared between Add and Edit)
  const renderForm = (onSubmit: () => void, submitLabel: string) => (
    <div className="space-y-4">
      <Input
        label="Description"
        value={formData.description}
        onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
        placeholder="What was this expense for?"
      />

      <Input
        label="Amount"
        type="number"
        value={formData.amount || ''}
        onChange={(e) => setFormData((f) => ({ ...f, amount: Number(e.target.value) }))}
        placeholder="0.00"
        min="0"
        step="0.01"
      />

      <Select
        label="Category"
        value={formData.category}
        onChange={(e) => setFormData((f) => ({ ...f, category: e.target.value }))}
        options={[{ value: '', label: 'Select category...' }, ...categoryOptions]}
      />

      <Input
        label="Date"
        type="date"
        value={formData.expenseDate}
        onChange={(e) => setFormData((f) => ({ ...f, expenseDate: e.target.value }))}
      />

      <div className="flex justify-end gap-3 pt-2">
        <Button
          variant="ghost"
          onClick={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setFormData(getInitialFormData());
          }}
        >
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={saving || !formData.description.trim() || formData.amount <= 0}>
          {saving ? <LoadingSpinner size="sm" /> : submitLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Receipt className="w-7 h-7 text-primary-500" />
            Expense Tracker
          </h1>
          <p className="text-dark-400 mt-1">Track and manage your expenses</p>
        </div>
        <Button
          onClick={() => {
            setFormData(getInitialFormData());
            setShowAddModal(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Expense
        </Button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto p-1 hover:text-red-300">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Date Navigation */}
      <Card>
        <CardContent className="py-3 px-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            {!useCustomRange ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={prevMonth}
                  className="p-2 hover:bg-dark-700 rounded-lg text-dark-300 hover:text-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="text-center min-w-[160px]">
                  <h2 className="text-lg font-semibold text-white">
                    {format(currentMonth, 'MMMM yyyy')}
                  </h2>
                </div>
                <button
                  onClick={nextMonth}
                  className="p-2 hover:bg-dark-700 rounded-lg text-dark-300 hover:text-white transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="bg-dark-700 border border-dark-600 rounded-lg px-3 py-1.5 text-sm text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
                <span className="text-dark-400">to</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="bg-dark-700 border border-dark-600 rounded-lg px-3 py-1.5 text-sm text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
              </div>
            )}
            <button
              onClick={() => {
                setUseCustomRange((v) => !v);
                if (!useCustomRange) {
                  setCustomStart(format(startOfMonth(currentMonth), 'yyyy-MM-dd'));
                  setCustomEnd(format(endOfMonth(currentMonth), 'yyyy-MM-dd'));
                }
              }}
              className="flex items-center gap-1.5 text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              <Calendar className="w-4 h-4" />
              {useCustomRange ? 'Monthly View' : 'Custom Range'}
            </button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-dark-400">Total Expenses</p>
                    <p className="text-2xl font-bold text-white mt-1">{formatAmount(total)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-red-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-dark-400">Transactions</p>
                    <p className="text-2xl font-bold text-white mt-1">{expenses.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-dark-400">Avg. per Transaction</p>
                    <p className="text-2xl font-bold text-white mt-1">
                      {expenses.length > 0
                        ? formatAmount(Math.round((total / expenses.length) * 100) / 100)
                        : formatAmount(0)}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    {displayCurrency === 'INR' ? (
                      <IndianRupee className="w-6 h-6 text-emerald-400" />
                    ) : (
                      <DollarSign className="w-6 h-6 text-emerald-400" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Category Breakdown */}
          {sortedCategories.length > 0 && (
            <Card>
              <CardHeader>
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <Tag className="w-4 h-4 text-primary-500" />
                  Category Breakdown
                </h3>
              </CardHeader>
              <CardContent className="space-y-3">
                {sortedCategories.map((cat) => (
                  <div key={cat.category} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-dark-200 flex items-center gap-2">
                        <span>{cat.icon}</span>
                        {cat.category}
                      </span>
                      <span className="text-dark-300 font-medium">
                        {formatAmount(cat.amount)}
                        <span className="text-dark-500 ml-2">({cat.percentage.toFixed(1)}%)</span>
                      </span>
                    </div>
                    <div className="w-full bg-dark-700 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${cat.color} transition-all duration-500`}
                        style={{ width: `${cat.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Expenses List */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <h3 className="text-base font-semibold text-white">
                  All Expenses ({displayExpenses.length})
                </h3>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search expenses..."
                    className="w-full pl-9 pr-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-sm text-white placeholder-dark-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {displayExpenses.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-dark-400">
                  <Receipt className="w-12 h-12 mb-3 opacity-30" />
                  <p className="text-sm">
                    {searchQuery ? 'No matching expenses found' : 'No expenses for this period'}
                  </p>
                  {!searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-3"
                      onClick={() => {
                        setFormData(getInitialFormData());
                        setShowAddModal(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add your first expense
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {/* Table Header (desktop) */}
                  <div className="hidden md:grid grid-cols-12 gap-3 px-4 sm:px-6 py-3 border-b border-dark-700 text-xs text-dark-400 uppercase tracking-wider">
                    <button
                      onClick={() => toggleSort('date')}
                      className="col-span-2 flex items-center gap-1 hover:text-white transition-colors text-left"
                    >
                      Date
                      {sortField === 'date' && (
                        <ArrowUpDown className="w-3 h-3" />
                      )}
                    </button>
                    <div className="col-span-3">Description</div>
                    <button
                      onClick={() => toggleSort('category')}
                      className="col-span-2 flex items-center gap-1 hover:text-white transition-colors text-left"
                    >
                      Category
                      {sortField === 'category' && (
                        <ArrowUpDown className="w-3 h-3" />
                      )}
                    </button>
                    <div className="col-span-2">Added By</div>
                    <button
                      onClick={() => toggleSort('amount')}
                      className="col-span-2 flex items-center gap-1 hover:text-white transition-colors text-left justify-end"
                    >
                      Amount
                      {sortField === 'amount' && (
                        <ArrowUpDown className="w-3 h-3" />
                      )}
                    </button>
                    <div className="col-span-1 text-right">Actions</div>
                  </div>

                  {/* Expense Rows */}
                  <div className="divide-y divide-dark-700/50">
                    {displayExpenses.map((expense) => (
                      <div
                        key={expense.id}
                        className="group px-4 sm:px-6 py-3 hover:bg-dark-700/30 transition-colors"
                      >
                        {/* Desktop Row */}
                        <div className="hidden md:grid grid-cols-12 gap-3 items-center">
                          <div className="col-span-2 text-sm text-dark-300">
                            {format(parseISO(expense.expenseDate), 'dd MMM yyyy')}
                          </div>
                          <div className="col-span-3 text-sm text-white font-medium truncate">
                            {expense.description}
                          </div>
                          <div className="col-span-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-dark-700 text-dark-200 border border-dark-600">
                              <span>{categoryIcons[expense.category] || '📦'}</span>
                              {expense.category || 'Uncategorized'}
                            </span>
                          </div>
                          <div className="col-span-2 text-sm text-dark-400 truncate">
                            {expense.ownerName}
                          </div>
                          <div className="col-span-2 text-sm font-semibold text-red-400 text-right">
                            {formatAmount(expense.displayAmount)}
                          </div>
                          <div className="col-span-1 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => openEditModal(expense)}
                              className="p-1.5 text-dark-400 hover:text-primary-400 hover:bg-dark-600 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            {!isFamilyMember && (
                              <button
                                onClick={() => openDeleteModal(expense)}
                                className="p-1.5 text-dark-400 hover:text-red-400 hover:bg-dark-600 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Mobile Card */}
                        <div className="md:hidden space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">
                                {expense.description}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-dark-700 text-dark-200 border border-dark-600">
                                  <span>{categoryIcons[expense.category] || '📦'}</span>
                                  {expense.category || 'Uncategorized'}
                                </span>
                                <span className="text-xs text-dark-500">
                                  {expense.ownerName}
                                </span>
                              </div>
                            </div>
                            <p className="text-sm font-semibold text-red-400 ml-3">
                              {formatAmount(expense.displayAmount)}
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-dark-500">
                              {format(parseISO(expense.expenseDate), 'dd MMM yyyy')}
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEditModal(expense)}
                                className="p-1.5 text-dark-400 hover:text-primary-400 hover:bg-dark-600 rounded-lg transition-colors"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              {!isFamilyMember && (
                                <button
                                  onClick={() => openDeleteModal(expense)}
                                  className="p-1.5 text-dark-400 hover:text-red-400 hover:bg-dark-600 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Add Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Expense">
        {renderForm(handleAdd, 'Add Expense')}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Expense">
        {renderForm(handleEdit, 'Save Changes')}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Expense"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-dark-300">
            Are you sure you want to delete this expense?
          </p>
          {selectedExpense && (
            <div className="p-3 bg-dark-700/50 rounded-lg border border-dark-600">
              <p className="text-white font-medium">{selectedExpense.description}</p>
              <p className="text-red-400 text-sm mt-1">{formatAmount(selectedExpense.amount)}</p>
              <p className="text-dark-500 text-xs mt-1">
                {format(parseISO(selectedExpense.expenseDate), 'dd MMM yyyy')}
              </p>
            </div>
          )}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={saving}>
              {saving ? <LoadingSpinner size="sm" /> : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
